import { useQuery } from '@tanstack/react-query';
import { fetchData, ValidationError } from '../../../utils';
import useError from '../../useError';
import useSmartlook from '../../useSmartlook';
import { LoanExtensionFee } from '../application/useActiveLoanQuery';

export function useClientOfferQuery(
  { amount, term }: { amount?: number; term?: number },
  enabled = true,
) {
  const { showError } = useError();
  const { error } = useSmartlook();

  return useQuery<ClientOffer>({
    queryKey: ['client', 'application', 'offer', { amount, term }] as const,
    queryFn: async () => {
      try {
        const resp = await fetchData(
          `/client/application/offer?amount=${amount}&term=${term}`,
          {
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/vnd.4finance.web.v1.hal+json',
            },
          },
        );

        return resp as ClientOffer;
      } catch (e: unknown) {
        error(
          '[useClientOfferQuery][GET /client/application/offer?amount={amount}&term={term}]',
          e,
        );
        if (
          e instanceof ValidationError &&
          e.fieldErrors[0].source !== 'CalculateExistingClientOfferCommand' &&
          e.fieldErrors[0].message !== 'not_in_range'
        ) {
          showError();
        }
        throw e;
      }
    },
    enabled: !!amount && !!term && enabled,
  });
}

export type ClientOffer = {
  term: number;
  newPrincipal: number;
  totalPrincipal: number;
  interestRate: number;
  newInterest: number;
  newInterestBeforeDiscount: number;
  firstPaymentInterest: number;
  currentPrincipal: number;
  currentPrincipalWithInterest: number;
  currentPrincipalWithInterestAndInitialCommission: number;
  monthlyPayment: number;
  agreementEndDate: string;
  isAdditionalAmount: boolean;
  discountAmount: number;
  discountPercent: number;
  discounts: {
    type: string;
    percent: number;
    amount: number;
  }[];
  firstPaymentDate: string;
  assignedInterest: number;
  annualPercentageRate: number;
  totalInterest: number;
  initialCommission: number;
  newInitialCommission: number;
  totalCostOfCredit: number;
  totalRepayableAmount: number;
  futureExtensionFees: LoanExtensionFee[];
  extensionFees: {
    7: number;
    14: number;
    30: number;
  };
  loanExtensionFees: LoanExtensionFee[];
  newAmountUsedDays: number;
  newAmountWithInterestAndInitialCommission: number;
  newPrincipalApr: number;
  totalRoundedAmount: number;
  capitalizableCommission: number;
  newPrincipalWithAllCapitalizableFees: number;
  newCapitalizedInterest: number;
  _links: {
    self: {
      href: string;
      title: string;
    };
  };
};
